import { Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import Button, { ButtonThemes } from 'ui-components/button/button';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import SlideOutSidebar from 'ui-components/slide-out-sidebar/slideOutSidebar';
import XGSFormDate from 'ui-components/form/date/xgsFormDate';
import { currentOrClosestBusinessDay } from 'hooks/utils';
import XGSFormInput from 'ui-components/form/input/xgsFormInput';
import '../cloneWeek.scss'
import { useCreateWeek, useUpdateWeek } from '../hooks/useWeek';
import { useSelector } from 'react-redux';
import { staticLoadSelector } from 'slices/static-load/staticLoadSlice';
import moment from 'moment';
import XGSFormSelect from 'ui-components/form/select/xgsFormSelect';
import { statusOptions } from 'app/data/static-load/model';

interface UpdateWeekSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  refetchGetWeeks: () => void;
  isEdit: boolean;
  editWeek: {
    weekStartDate: string;
    division: string;
    weekDescription: string;
    status: string;
    dispatches?: number;
    createdAt?: string;
    lastModifiedAt?: string;
  }
}

const UpdateWeek: React.FC<UpdateWeekSidebarProps> = ({ isOpen,
  onClose, refetchGetWeeks, isEdit, editWeek }) => {
  const [newWeekStartDate, setNewWeekStartDate] = useState<string | undefined>();

  const staticLoadSelectorData = useSelector(staticLoadSelector);
  const selectedDivision = staticLoadSelectorData.selectedDivision;

  const createWeekMutation = useCreateWeek(() => {
    refetchGetWeeks();
    onClose();
  });

  const updateWeekMutation = useUpdateWeek(() => {
    refetchGetWeeks();
    onClose();
  });

  const isSaving = isEdit ? updateWeekMutation.isLoading : createWeekMutation.isLoading;

  useEffect(() => {
    if (isOpen && isEdit) {
      setNewWeekStartDate(moment(editWeek.weekStartDate).format('MM/DD/YYYY'));
    } else if (!isOpen) {
      setNewWeekStartDate(undefined);
    }
  }, [isOpen, isEdit, editWeek.weekStartDate]);

  const weekStartMoment = moment(editWeek.weekStartDate);
  const weekEndMoment = weekStartMoment.clone().add(6, 'days');

  return <SlideOutSidebar
    show={isOpen}
    onClose={onClose}
    header={isEdit ? "Edit Schedule" : "Create New Schedule"}
    size="small"
    className="filter-sidebar"
  >
    <Formik
      initialValues={{
        newWeekStartDate: isEdit ? editWeek.weekStartDate : "",
        weekDescription: isEdit ? editWeek.weekDescription : "",
        status: isEdit ? editWeek.status : ""
      }} onSubmit={(values) => {
        if (isEdit) {
          updateWeekMutation.mutate({
            division: editWeek.division,
            weekStartDate: editWeek.weekStartDate,
            weekDescription: values.weekDescription.trim(),
            status: values.status.trim()
          })
        }
        else {
          createWeekMutation.mutate({
            division: selectedDivision,
            weekStartDate: moment(newWeekStartDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
            weekDescription: values.weekDescription.trim(),
            status: values.status.trim()
          });
        }

      }}
      enableReinitialize
    >
      {(formikProps: FormikProps<any>) => (
        <Form>
          {!isEdit && <>
            <div className='week-configuration-title'>Week configuration</div>
            <div style={{ marginTop: "10px" }} />
          </>
          }
          {!isEdit && <>
            <XGSFormDate
              name="weekStartDate"
              label="Week Start Date:"
              placeholder="Select date..."
              onDateChange={(date) => { setNewWeekStartDate(date) }}
              value={newWeekStartDate}
              onChange={() => null}
              minDate={new Date(currentOrClosestBusinessDay())}
              required={true}
              labelMode={LabelModes.column}
              filterDate={(date) => date.getDay() === 1}
              disableWeekends={true}
            />
            <div style={{ marginTop: "10px" }} />
          </>
          }
          {isEdit && <>
            <div className="week-description">
              Week: {weekStartMoment.format("MMM D")} – {weekEndMoment.format("MMM D")}, {weekStartMoment.format("YYYY")}
            </div></>}
          <div style={{ marginTop: "10px" }} />
          <XGSFormInput
            type="text"
            name="weekDescription"
            labelMode={LabelModes.column}
            label="Description"
          />
          {isEdit && <>
            <div style={{ marginTop: "10px" }} />
            <XGSFormSelect
              label="Status"
              name="status"
              options={statusOptions}
              value={
                formikProps.values.status
                  ? statusOptions.find((option) => option.value === formikProps.values.status)
                  : null
              }
              onValueChange={(option) => {
                formikProps.setFieldValue("status", option?.value ?? "");
              }}
              formik
              // required={true}
              // className="add-edit-address-section__width-50"
              labelMode={LabelModes.column}
            />
            <div style={{ marginTop: "10px" }} />
            <div className='schedule-description-container'>
              <div>
                <div className='schedule-description-container__label'>Week Range</div>
                <div className='schedule-description-container__value'>
                  {weekStartMoment.format('MMM D')} – {weekEndMoment.format('MMM D, YYYY')}
                </div>
              </div>
              <div>
                <div className='schedule-description-container__label'>Created</div>
                <div className='schedule-description-container__value'>
                  {editWeek.createdAt ? moment(editWeek.createdAt).format('MMM D, YYYY') : '—'}
                </div>
              </div>
              <div>
                <div className='schedule-description-container__label'>Dispatches</div>
                <div className='schedule-description-container__value'>
                  {editWeek.dispatches != null ? `${editWeek.dispatches} dispatches` : '—'}
                </div>
              </div>
              <div>
                <div className='schedule-description-container__label'>Last Modified</div>
                <div className='schedule-description-container__value'>
                  {editWeek.lastModifiedAt ? moment(editWeek.lastModifiedAt).format('MMM D, YYYY') : '—'}
                </div>
              </div>
            </div></>}
          <div style={{ marginTop: "10px" }} />
          <div className="copy-move-lane-actions">
            <Button type="button" theme={ButtonThemes.gray} onClick={onClose}>Cancel</Button>
            <Button type="submit" theme={ButtonThemes.blue} spinner={isSaving} disabled={isSaving || (isEdit ? !formikProps.dirty : !newWeekStartDate)}>Save Changes</Button>
          </div>
        </Form>
      )}
    </Formik>
  </SlideOutSidebar >;
};

export default UpdateWeek;
